/**
 * Radio Browser API mirror discovery and failover. Environment-agnostic
 * (`fetch` + `AbortSignal`), so the browser directory and the server's
 * station resolver share it.
 */

/** Verified hosts to discover the live mirror list from. Revalidate before deploys. */
export const RADIO_BOOTSTRAP_HOSTS: readonly string[] = [
  "de1.api.radio-browser.info",
  "de2.api.radio-browser.info",
];

const MIRROR_HOST_RE = /^[a-z0-9-]+\.api\.radio-browser\.info$/i;
const DISCOVERY_TTL_MS = 24 * 60 * 60 * 1000;

/** Only named Radio Browser API hosts; never raw IPs or other domains. */
export function isAllowedMirrorHost(host: unknown): host is string {
  return typeof host === "string" && MIRROR_HOST_RE.test(host);
}

/** A Fisher–Yates shuffled copy. */
export function shuffle<T>(items: readonly T[], random: () => number = Math.random): T[] {
  const out = [...items];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [out[i], out[j]] = [out[j]!, out[i]!];
  }
  return out;
}

/** Every tried mirror failed (timeout, network/CORS, 429/5xx or malformed JSON). */
export class MirrorError extends Error {}

export interface MirrorCache {
  load(): { hosts: string[]; savedAt: number } | null;
  save(hosts: string[], savedAt: number): void;
}

export interface MirrorPoolOptions {
  fetch?: typeof fetch;
  /** Per-mirror request timeout. */
  timeoutMs?: number;
  /** Mirrors tried per call before giving up. */
  maxMirrors?: number;
  /** Extra request headers (the server identifies itself with a User-Agent). */
  headers?: Record<string, string>;
  /** Persists the discovered mirror list (the client uses localStorage). */
  cache?: MirrorCache;
  bootstrapHosts?: readonly string[];
  now?: () => number;
  random?: () => number;
}

export type QueryParams = Record<string, string | number | boolean>;

/** Discovers mirrors once, then fails over between them for each request. */
export class MirrorPool {
  private readonly fetchFn: typeof fetch;
  private readonly timeoutMs: number;
  private readonly maxMirrors: number;
  private readonly headers: Record<string, string>;
  private readonly bootstrap: readonly string[];
  private readonly now: () => number;
  private readonly random: () => number;
  private hosts: string[] = [];
  private discoveredAt = 0;
  private discovery?: Promise<string[]>;

  constructor(private readonly options: MirrorPoolOptions = {}) {
    this.fetchFn = options.fetch ?? ((input, init) => fetch(input, init));
    this.timeoutMs = options.timeoutMs ?? 6000;
    this.maxMirrors = options.maxMirrors ?? 3;
    this.headers = { Accept: "application/json", ...options.headers };
    this.bootstrap = options.bootstrapHosts ?? RADIO_BOOTSTRAP_HOSTS;
    this.now = options.now ?? Date.now;
    this.random = options.random ?? Math.random;
  }

  /** GET `path` as JSON from the first mirror that answers. */
  async getJson(path: string, params: QueryParams = {}, signal?: AbortSignal): Promise<unknown> {
    const hosts = await this.mirrors(signal);
    try {
      const { value, host } = await this.tryHosts(hosts, path, params, signal);
      // Stick with a mirror that works.
      if (this.hosts[0] !== host && this.hosts.includes(host)) {
        this.hosts = [host, ...this.hosts.filter((h) => h !== host)];
      }
      return value;
    } catch (error) {
      // Rediscover next time rather than retrying the same dead hosts.
      if (error instanceof MirrorError) this.discoveredAt = 0;
      throw error;
    }
  }

  /** The current mirror list: memory, then cache, then discovery, then bootstrap. */
  async mirrors(signal?: AbortSignal): Promise<string[]> {
    const now = this.now();
    if (this.hosts.length > 0 && now - this.discoveredAt < DISCOVERY_TTL_MS) return this.hosts;

    if (this.hosts.length === 0) {
      const cached = this.readCache();
      if (cached && now - cached.savedAt < DISCOVERY_TTL_MS) {
        this.hosts = shuffle(cached.hosts, this.random);
        this.discoveredAt = cached.savedAt;
        return this.hosts;
      }
    }

    this.discovery ??= this.discover(signal).finally(() => {
      this.discovery = undefined;
    });
    return this.discovery;
  }

  private async discover(signal?: AbortSignal): Promise<string[]> {
    const fallback = this.hosts.length > 0 ? this.hosts : shuffle(this.bootstrap, this.random);
    try {
      const { value } = await this.tryHosts(
        shuffle(this.bootstrap, this.random),
        "/json/servers",
        {},
        signal,
      );
      const names = new Set<string>();
      if (Array.isArray(value)) {
        for (const server of value) {
          const name = (server as { name?: unknown } | null)?.name;
          if (isAllowedMirrorHost(name)) names.add(name.toLowerCase());
        }
      }
      if (names.size === 0) throw new MirrorError("no usable mirrors listed");
      this.hosts = shuffle([...names], this.random);
      this.discoveredAt = this.now();
      this.writeCache(this.hosts, this.discoveredAt);
    } catch (error) {
      if (signal?.aborted) throw error;
      // Keep what we had (or the bootstrap list) and try again after a while.
      this.hosts = fallback;
      this.discoveredAt = this.now() - DISCOVERY_TTL_MS + 5 * 60 * 1000;
    }
    return this.hosts;
  }

  private async tryHosts(
    hosts: readonly string[],
    path: string,
    params: QueryParams,
    signal?: AbortSignal,
  ): Promise<{ value: unknown; host: string }> {
    let lastError: unknown;
    for (const host of hosts.slice(0, this.maxMirrors)) {
      signal?.throwIfAborted();
      const url = new URL(path, `https://${host}`);
      for (const [key, value] of Object.entries(params)) url.searchParams.set(key, String(value));
      const timeout = AbortSignal.timeout(this.timeoutMs);
      try {
        const response = await this.fetchFn(url, {
          headers: this.headers,
          signal: signal ? AbortSignal.any([signal, timeout]) : timeout,
        });
        if (!response.ok) throw new MirrorError(`${host} answered ${response.status}`);
        return { value: await response.json(), host };
      } catch (error) {
        if (signal?.aborted) throw signal.reason ?? error;
        lastError = error;
      }
    }
    throw new MirrorError(`all radio directory mirrors failed: ${String(lastError)}`);
  }

  private readCache(): { hosts: string[]; savedAt: number } | null {
    try {
      const cached = this.options.cache?.load();
      if (!cached || !Array.isArray(cached.hosts) || typeof cached.savedAt !== "number") {
        return null;
      }
      const hosts = cached.hosts.filter(isAllowedMirrorHost);
      return hosts.length > 0 ? { hosts, savedAt: cached.savedAt } : null;
    } catch {
      return null;
    }
  }

  private writeCache(hosts: string[], savedAt: number): void {
    try {
      this.options.cache?.save(hosts, savedAt);
    } catch {
      // The cache is a resilience measure only.
    }
  }
}
